/**
 * GuideOverlay — renders draggable ruler guides and handles creating new ones.
 *
 * Guides are stored as world-space positions. H-guides are horizontal lines (dragged
 * from the horizontal ruler), V-guides are vertical lines (dragged from the vertical ruler).
 *
 * Usage:
 *  - Drag from ruler → creates a new guide at the pointer position
 *  - Drag an existing guide → repositions it
 *  - Double-click a guide → removes it
 */
import { useState, useRef, useCallback } from 'react';
import { nanoid } from 'nanoid';
import type { ViewportState } from '../../lib/spine-manager';

export interface Guide {
  id: string;
  type: 'h' | 'v'; // 'h' = horizontal line at worldY, 'v' = vertical line at worldX
  worldPos: number;
}

const RULER_SIZE = 20; // must match SceneCanvas
const GUIDE_COLOR = 'rgba(99,190,255,0.75)';
const GUIDE_COLOR_HOVER = 'rgba(60,160,255,0.95)';
const GUIDE_THICKNESS = 1;

interface GuideOverlayProps {
  guides: Guide[];
  vpState: ViewportState;
  containerW: number;
  containerH: number;
  onGuidesChange: (guides: Guide[]) => void;
}

export default function GuideOverlay({ guides, vpState, containerW, containerH, onGuidesChange }: GuideOverlayProps) {
  const { zoom, x: vpX, y: vpY } = vpState;
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [hoverGuideId, setHoverGuideId] = useState<string | null>(null);
  const dragStart = useRef<{ clientPos: number; worldPos: number } | null>(null);

  const worldToScreenH = (worldPos: number) => worldPos * zoom + vpY + RULER_SIZE;
  const worldToScreenV = (worldPos: number) => worldPos * zoom + vpX + RULER_SIZE;

  const startGuideDrag = useCallback((e: React.PointerEvent, id: string, type: 'h' | 'v') => {
    e.preventDefault();
    e.stopPropagation();
    (e.target as Element).setPointerCapture(e.pointerId);
    const guide = guides.find((g) => g.id === id);
    if (!guide) return;
    setDraggingId(id);
    dragStart.current = {
      clientPos: type === 'h' ? e.clientY : e.clientX,
      worldPos: guide.worldPos,
    };
  }, [guides]);

  const onPointerMove = useCallback((e: React.PointerEvent, id: string, type: 'h' | 'v') => {
    if (draggingId !== id || !dragStart.current) return;
    const clientPos = type === 'h' ? e.clientY : e.clientX;
    const delta = (clientPos - dragStart.current.clientPos) / zoom;
    const newWorldPos = dragStart.current.worldPos + delta;
    onGuidesChange(guides.map((g) => g.id === id ? { ...g, worldPos: newWorldPos } : g));
  }, [draggingId, zoom, guides, onGuidesChange]);

  const onPointerUp = useCallback((id: string) => {
    setDraggingId(null);
    dragStart.current = null;
    // Remove guide if dragged back into the ruler area
    const guide = guides.find((g) => g.id === id);
    if (!guide) return;
    const screenPos = guide.type === 'h' ? worldToScreenH(guide.worldPos) : worldToScreenV(guide.worldPos);
    if (screenPos < RULER_SIZE) {
      onGuidesChange(guides.filter((g) => g.id !== id));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draggingId, guides, onGuidesChange, vpX, vpY, zoom]);

  const removeGuide = useCallback((id: string) => {
    onGuidesChange(guides.filter((g) => g.id !== id));
  }, [guides, onGuidesChange]);

  return (
    <>
      {guides.map((guide) => {
        const isHover = hoverGuideId === guide.id;
        const isDragging = draggingId === guide.id;
        const color = (isHover || isDragging) ? GUIDE_COLOR_HOVER : GUIDE_COLOR;
        const screenPos = guide.type === 'h' ? worldToScreenH(guide.worldPos) : worldToScreenV(guide.worldPos);
        const label = Math.round(guide.worldPos);

        if (guide.type === 'h') {
          // Horizontal guide — drawn as a horizontal line
          const top = screenPos;
          if (top < RULER_SIZE || top > containerH) return null;
          return (
            <div
              key={guide.id}
              data-testid={`guide-h-${guide.id}`}
              className="absolute pointer-events-auto select-none"
              style={{
                top: top - GUIDE_THICKNESS / 2,
                left: RULER_SIZE,
                right: 0,
                height: Math.max(GUIDE_THICKNESS, isDragging ? 6 : 4),
                cursor: 'ns-resize',
                zIndex: 45,
              }}
              onPointerDown={(e) => startGuideDrag(e, guide.id, 'h')}
              onPointerMove={(e) => onPointerMove(e, guide.id, 'h')}
              onPointerUp={() => onPointerUp(guide.id)}
              onPointerEnter={() => setHoverGuideId(guide.id)}
              onPointerLeave={() => setHoverGuideId(null)}
              onDoubleClick={() => removeGuide(guide.id)}
            >
              <div className="absolute inset-x-0" style={{ top: '50%', height: 1, background: color, transform: 'translateY(-50%)' }} />
              <div
                className="absolute left-1 font-mono text-[9px] px-1 py-0 rounded pointer-events-none"
                style={{ top: '50%', transform: 'translateY(-50%)', color, background: 'rgba(16,16,24,0.75)' }}
              >
                {label}
              </div>
            </div>
          );
        } else {
          // Vertical guide — drawn as a vertical line
          const left = screenPos;
          if (left < RULER_SIZE || left > containerW) return null;
          return (
            <div
              key={guide.id}
              data-testid={`guide-v-${guide.id}`}
              className="absolute pointer-events-auto select-none"
              style={{
                left: left - GUIDE_THICKNESS / 2,
                top: RULER_SIZE,
                bottom: 0,
                width: Math.max(GUIDE_THICKNESS, isDragging ? 6 : 4),
                cursor: 'ew-resize',
                zIndex: 45,
              }}
              onPointerDown={(e) => startGuideDrag(e, guide.id, 'v')}
              onPointerMove={(e) => onPointerMove(e, guide.id, 'v')}
              onPointerUp={() => onPointerUp(guide.id)}
              onPointerEnter={() => setHoverGuideId(guide.id)}
              onPointerLeave={() => setHoverGuideId(null)}
              onDoubleClick={() => removeGuide(guide.id)}
            >
              <div className="absolute inset-y-0" style={{ left: '50%', width: 1, background: color, transform: 'translateX(-50%)' }} />
              <div
                className="absolute font-mono text-[9px] px-1 py-0 rounded pointer-events-none"
                style={{
                  top: 4,
                  left: '50%',
                  transform: 'translateX(-50%) rotate(90deg)',
                  transformOrigin: 'center center',
                  color,
                  background: 'rgba(16,16,24,0.75)',
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </div>
            </div>
          );
        }
      })}
    </>
  );
}

/** Hook to handle ruler drag → new guide creation */
export function useRulerGuideDrag(
  vpState: ViewportState,
  guides: Guide[],
  onGuidesChange: (guides: Guide[]) => void,
) {
  const { zoom, x: vpX, y: vpY } = vpState;
  const pendingRef = useRef<{ id: string; type: 'h' | 'v' } | null>(null);
  const captureRef = useRef<Element | null>(null);

  const startHRulerDrag = useCallback((e: React.PointerEvent) => {
    // Only start if pointer is moving down (dragging into canvas)
    e.preventDefault();
    const id = nanoid(6);
    const worldY = (e.clientY - e.currentTarget.getBoundingClientRect().top - vpY) / zoom;
    const newGuide: Guide = { id, type: 'h', worldPos: worldY };
    onGuidesChange([...guides, newGuide]);
    pendingRef.current = { id, type: 'h' };
    captureRef.current = e.currentTarget;
    (e.target as Element).setPointerCapture(e.pointerId);
  }, [guides, onGuidesChange, vpY, zoom]);

  const startVRulerDrag = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    const id = nanoid(6);
    const worldX = (e.clientX - e.currentTarget.getBoundingClientRect().left - vpX) / zoom;
    const newGuide: Guide = { id, type: 'v', worldPos: worldX };
    onGuidesChange([...guides, newGuide]);
    pendingRef.current = { id, type: 'v' };
    captureRef.current = e.currentTarget;
    (e.target as Element).setPointerCapture(e.pointerId);
  }, [guides, onGuidesChange, vpX, zoom]);

  const onRulerPointerMove = useCallback((e: React.PointerEvent) => {
    if (!pendingRef.current) return;
    const { id, type } = pendingRef.current;
    let worldPos: number;
    if (type === 'h') {
      const rect = (captureRef.current as HTMLElement).getBoundingClientRect();
      worldPos = (e.clientY - rect.top - RULER_SIZE - vpY) / zoom;
    } else {
      const rect = (captureRef.current as HTMLElement).getBoundingClientRect();
      worldPos = (e.clientX - rect.left - RULER_SIZE - vpX) / zoom;
    }
    onGuidesChange(guides.map((g) => g.id === id ? { ...g, worldPos } : g));
  }, [guides, onGuidesChange, vpX, vpY, zoom]);

  const onRulerPointerUp = useCallback((e: React.PointerEvent) => {
    if (!pendingRef.current) return;
    const { id, type } = pendingRef.current;
    // If released back in the ruler zone, remove the guide
    const rect = (captureRef.current as HTMLElement)?.getBoundingClientRect();
    if (rect) {
      const inRuler = type === 'h' ? e.clientY < rect.bottom : e.clientX < rect.right;
      if (inRuler) {
        onGuidesChange(guides.filter((g) => g.id !== id));
      }
    }
    pendingRef.current = null;
    captureRef.current = null;
  }, [guides, onGuidesChange]);

  return { startHRulerDrag, startVRulerDrag, onRulerPointerMove, onRulerPointerUp };
}
